import React, { useState } from "react";
import {
  HorizontalBar,
  VerticalBar,
  VerticalBarMulti,
  StackedBar,
  MyPie,
  BoxPlot,
  WordCloud,
  ScatterWeighted,
  Sankey,
} from "../charts";
import { GraphSubsection, QuoteSubsection } from "../components";
import * as Colors from "../Colors";

function getGraph(info, data) {
  switch (info.type) {
    case "HORIZONTAL_BAR":
      return <HorizontalBar data={data} options={info} />;
    case "VERTICAL_BAR":
      return <VerticalBar data={data} options={info} />;
    case "VERTICAL_BAR_MULTI":
      return <VerticalBarMulti data={data} options={info} />;
    case "STACKED_BAR":
      return <StackedBar data={data} options={info} />;
    case "PIE":
      return <MyPie data={data} options={info} />;
    case "BOX_PLOT":
      return <BoxPlot data={data} options={info} />;
    case "WORD_CLOUD":
      return <WordCloud data={data} options={info} />;
    case "WORD_CLOUD_MULTI":
      return <WordCloudMulti data={data} options={info} />;
    case "BOX_PLOT_MULTI":
      return <BoxPlotMulti data={data} options={info} />;
    case "SCATTER_WEIGHTED":
      return <ScatterWeighted data={data} options={info} />;
    case "HORIZONTAL_BAR_MULTI":
      return <HorizontalBarMulti data={data} options={info} />;
    case "SANKEY":
      return <Sankey data={data} options={info} />;
    default:
      return null;
  }
}

function getSubsection(info, data, key) {
  if (info.type === "QUOTE") {
    return (
      <QuoteSubsection title={info.title} quotes={data} key={key}>
        {info.description}
      </QuoteSubsection>
    );
  } else {
    return (
      <GraphSubsection
        title={info.title}
        graph={getGraph(info, data)}
        fullWidth={info.fullWidth}
        key={key}
        height={info.height}
      >
        {info.description}
      </GraphSubsection>
    );
  }
}

export function getContent(info, data) {
  return Object.keys(info).map((key) =>
    getSubsection(info[key], data[key], key)
  );
}

export function printMissingGraphs(data, info) {
  return (
    <ul>
      {Object.keys(data)
        .filter((key) => !(key in info))
        .map((key) => {
          return <li key={key}>{key}</li>;
        })}
    </ul>
  );
}

function CircleButton({ text, color, onClick }) {
  return (
    <div onClick={onClick} className="CircleLegend">
      <div className="Circle" style={{ backgroundColor: color }}></div>
      <div className="CircleLegendText">{text}</div>
    </div>
  );
}

export function WordCloudMulti({ data, options }) {
  const [keyIndex, setKeyIndex] = useState(0);

  return (
    <React.Fragment>
      <MultiLegend
        data={data}
        options={options}
        active={keyIndex}
        onClick={setKeyIndex}
        colors={[Colors.PURPLE]}
      />
      <WordCloud data={data[Object.keys(data)[keyIndex]]} options={options} />
    </React.Fragment>
  );
}

export function MultiLegend({ data, options, active, onClick, colors }) {
  const dataLabels = options.dataLabel ?? [];
  return (
    <div className="LegendContainer">
      {Object.keys(data).map((key, index) => {
        const color =
          index === active ? colors[index % colors.length] : Colors.INACTIVE;
        return (
          <CircleButton
            key={index}
            text={dataLabels[index]}
            color={color}
            onClick={() => onClick(index)}
          />
        );
      })}
    </div>
  );
}

export function BoxPlotMulti({ data, options }) {
  const [keyIndex, setKeyIndex] = useState(0);
  return (
    <React.Fragment>
      <MultiLegend
        data={data}
        options={options}
        active={keyIndex}
        onClick={setKeyIndex}
        colors={[Colors.ORANGE]}
      />
      <BoxPlot data={data[Object.keys(data)[keyIndex]]} options={options} />
    </React.Fragment>
  );
}

export function HorizontalBarMulti({ data, options }) {
  const [keyIndex, setKeyIndex] = useState(0);
  return (
    <React.Fragment>
      <MultiLegend
        data={data}
        options={options}
        active={keyIndex}
        onClick={setKeyIndex}
        colors={[Colors.ORANGE]}
      />
      <HorizontalBar
        data={data[Object.keys(data)[keyIndex]]}
        options={options}
      />
    </React.Fragment>
  );
}
